import React, { useEffect, useState } from "react";
import { Table, Tag, Card, Input, Row, Col, Button, Space, Modal, Divider, Avatar, Select } from "antd";
import { EyeOutlined, UserOutlined, SendOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useMessage from "antd/es/message/useMessage";
import { ComplaintService } from "../../Services/Manage/ComplaintService";

const replyQuillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    ["blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
};

function ComplaintRaiseList() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [messageApi, messageContxt] = useMessage();

  const [viewVisible, setViewVisible] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const getAllComplaints = async () => {
    setLoading(true);
    try {
      const request = await ComplaintService.getAllComplaints();
      const formattedData = request.data.map((item, index) => ({
        ...item,
        si_no: index + 1,
        key: item.id,
      }));
      setData(formattedData);
      filterData(searchText, statusFilter, formattedData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllComplaints();
  }, []);

  const filterData = (search, status, source = data) => {
    let result = source;
    if (status !== 'all') result = result.filter((item) => item.status === status);
    if (search) result = result.filter(
      (item) =>
        item.title?.toLowerCase().includes(search) ||
        item.description?.toLowerCase().includes(search) ||
        item.raised_by_name?.toLowerCase().includes(search) ||
        item.status?.toLowerCase().includes(search)
    );
    setFilteredData(result);
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    filterData(value, statusFilter);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    filterData(searchText, status);
  };

  const handleView = async (record) => {
    setViewItem(record);
    setReplyOpen(false);
    setReplyText(record.reply || "");
    setViewVisible(true);
    if (record.status === "sent") {
      try {
        await ComplaintService.updateComplaintStatus(record.id, { status: "read" });
        setFilteredData((prev) =>
          prev.map((item) => item.id === record.id ? { ...item, status: "read" } : item)
        );
        setData((prev) =>
          prev.map((item) => item.id === record.id ? { ...item, status: "read" } : item)
        );
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleSendReply = async () => {
    if (!replyText || replyText === "<p><br></p>") {
      messageApi.warning("Please write a reply");
      return;
    }
    setSending(true);
    try {
      const res = await ComplaintService.replyComplaint(viewItem.id, { reply: replyText });
      if (res.status) {
        messageApi.success(res.message);
        setReplyOpen(false);
        setViewItem((prev) => ({ ...prev, reply: replyText, replied_on: new Date(), status: "replied" }));
        setFilteredData((prev) =>
          prev.map((item) => item.id === viewItem.id ? { ...item, status: "replied" } : item)
        );
        setData((prev) =>
          prev.map((item) => item.id === viewItem.id ? { ...item, status: "replied" } : item)
        );
        getAllComplaints();
      } else {
        messageApi.error(res.message);
      }
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      : "-";

  const columns = [
    { title: "S. No", dataIndex: "si_no", key: "si_no", align: "center" },
    { title: "Subject", dataIndex: "title", key: "title" },
    // {
    //   title: "Description",
    //   dataIndex: "description",
    //   key: "description",
    //   render: (html) => {
    //     if (!html) return "-";
    //     const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    //     return (
    //       <div
    //         style={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    //         title={text}
    //       >
    //         {text || "-"}
    //       </div>
    //     );
    //   },
    // },
    { title: "Raised By", dataIndex: "raised_by_name", key: "raised_by_name", render: (name) => name || "-" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = { sent: "orange", read: "default", replied: "green" };
        return <Tag color={colorMap[status] || "default"}>{status === "sent" ? "UNREAD" : status ? status.toUpperCase() : "-"}</Tag>;
      },
    },
    { title: "Added On", dataIndex: "added_on", key: "added_on", render: (value) => formatDateTime(value) },
    { title: "Updated On", dataIndex: "updated_on", key: "updated_on", render: (value) => formatDateTime(value) },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (text, record) => (
        <Space size="middle">
          <Button size="large" icon={<EyeOutlined />} onClick={() => handleView(record)} />
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col><h3 style={{ margin: 0 }}>Complaint Raise</h3></Col>
          <Col>
            <Space>
              <Select
                size="large"
                value={statusFilter}
                onChange={handleStatusFilter}
                style={{ width: 150 }}
                showSearch
                allowClear
                onClear={() => handleStatusFilter('all')}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'sent', label: 'Unread' },
                  { value: 'read', label: 'Read' },
                  { value: 'replied', label: 'Replied' },
                ]}
              />
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={handleSearch}
                allowClear
                size="large"
                style={{ width: 250 }}
              />
            </Space>
          </Col>
        </Row>
      }
    >
      {messageContxt}
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={true}
        loading={loading}
        scroll={{ x: "max-content" }}
      />

      {/* Email-style View Modal */}
      <Modal
        title={null}
        open={viewVisible}
        onCancel={() => setViewVisible(false)}
        footer={null}
        width={720}
        styles={{ body: { paddingTop: 8 } }}
      >
        {viewItem && (
          <div>
            {/* Subject / title */}
            <h2 style={{ margin: "8px 0 16px", fontWeight: 600 }}>
              {viewItem.title || "Complaint"}
            </h2>

            {/* Sender row */}
            <Row align="middle" justify="space-between" style={{ marginBottom: 8 }}>
              <Col>
                <Space>
                  <Avatar icon={<UserOutlined />} style={{ backgroundColor: "#1677ff" }} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{viewItem.raised_by_name || "Unknown"}</div>
                    <div style={{ fontSize: 12, color: "#999" }}>
                      {formatDateTime(viewItem.added_on)}
                    </div>
                  </div>
                </Space>
              </Col>
              <Col>
                {(() => {
                  const colorMap = { sent: "blue", read: "default", replied: "green" };
                  return (
                    <Tag color={colorMap[viewItem.status] || "default"}>
                      {viewItem.status ? viewItem.status.toUpperCase() : "-"}
                    </Tag>
                  );
                })()}
              </Col>
            </Row>

            {/* Original complaint body */}
            <div
              className="ql-editor"
              style={{ padding: 0, fontSize: 15, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: viewItem.description || "-" }}
            />

            {/* Existing reply, shown like a threaded email */}
            {viewItem.reply && (
              <>
                <Divider />
                <div
                  style={{
                    borderLeft: "3px solid #1677ff",
                    background: "#f6f9ff",
                    borderRadius: 4,
                    padding: "12px 16px",
                  }}
                >
                  <Space style={{ marginBottom: 6 }}>
                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: "#52c41a" }} />
                    <span style={{ fontWeight: 500 }}>Reply</span>
                    <span style={{ fontSize: 12, color: "#999" }}>
                      {formatDateTime(viewItem.replied_on)}
                    </span>
                  </Space>
                  <div
                    className="ql-editor"
                    style={{ padding: 0, fontSize: 14 }}
                    dangerouslySetInnerHTML={{ __html: viewItem.reply }}
                  />
                </div>
              </>
            )}

            <Divider />

            {/* Reply action / editor */}
            {!replyOpen ? (
              <Space>
                <Button type="primary" onClick={() => setReplyOpen(true)}>
                  {viewItem.reply ? "Edit Reply" : "Reply"}
                </Button>
                <Button onClick={() => setViewVisible(false)}>Close</Button>
              </Space>
            ) : (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>
                  Reply to {viewItem.raised_by_name || "complaint"}
                </div>
                <div className="reply-quill" style={{ border: "1px solid #d9d9d9", borderRadius: 6 }}>
                  <ReactQuill
                    theme="snow"
                    value={replyText}
                    onChange={setReplyText}
                    modules={replyQuillModules}
                    placeholder="Type your reply..."
                  />
                </div>
                <Space style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sending}
                    onClick={handleSendReply}
                  >
                    Send
                  </Button>
                  <Button onClick={() => setReplyOpen(false)}>Cancel</Button>
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}

export default ComplaintRaiseList;